<template>
    <article>
        <p>
            When I look at Postgres dashboards provided by popular observability tools,
            I struggle to comprehend how they can be used to understand what's happening with a Postgres server.
            While they present numerous Postgres metrics, these metrics, unfortunately, prove useless
            when trying to troubleshoot the most common Postgres performance issues, such as high query latency and connection errors.
        </p>

        <v-row class="my-6">
            <v-col>
                <v-img eager max-width="500px" class="ma-auto" src="/static/img/blog/pg-issues/grafana_pg.png" />
                <div class="caption grey--text text-center">Image source: grafana.com</div>
            </v-col>
            <v-col>
                <v-img eager max-width="500px" class="ma-auto" src="/static/img/blog/pg-issues/datadog_pg.png" />
                <div class="caption grey--text text-center">Image source: datadoghq.com</div>
            </v-col>
        </v-row>

        <p>
            From my perspective, the best way to ensure that your dashboards and alerts can actually help you spot real-world problems
            is by deliberately introducing those issues into a testing environment.

            At Coroot, we consistently use this approach across all stages of product development – starting from gathering the right metrics
            to validating issue-detection algorithms built upon the collected metrics.
        </p>

        <p>
            Let's reproduce various Postgres failure scenarios and see how they can be identified with
            <a href="http://github.com/coroot/coroot" target="_blank">Coroot</a>.
        </p>

        <h2>Setting up the environment</h2>

        <p>
            Given that troubleshooting usually begins not from the database itself, but rather from an application
            that is not performing as expected, my testing environment will include both the database and
            the application using it.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/service-map.png" />
        </v-card>

        <p>
            My testing environment is a K3S cluster with 3 worker nodes (4xCPU/8Gb).
        </p>
        
        <p>
            As a database, I will use a Postgres cluster managed by the <a href="https://github.com/zalando/postgres-operator" target="_blank">Postgres Operator</a>.
            Each Postgres instance is instrumented by <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">coroot-pg-agent</a> deployed as a side-car container.
        </p>

        <pre v-hl.yaml>
apiVersion: "acid.zalan.do/v1"
kind: postgresql
metadata:
  name: db-main
  namespace: default
spec:
  ...
  volume:
    size: 30Gi
  numberOfInstances: 3
  users:
    coroot: [superuser]
    demo: []
  databases:
    demo: demo
  resources:
    limits:
      cpu: "2"
      memory: 4Gi
    requests:
      cpu: "2"
      memory: 4Gi
  postgresql:
    version: "14"
    parameters:
      track_io_timing: "on"
      log_destination: "stderr"
      logging_collector: "off"
      log_line_prefix: "%m [%p] "
  sidecars:
    - name: coroot-pg-agent
      image: ghcr.io/coroot/coroot-pg-agent:1.2.4
      env:
        - name: LISTEN
          value: ":9090"
        - name: PGPASSWORD
          valueFrom: { secretKeyRef: { name: coroot.db-main.credentials.postgresql.acid.zalan.do, key: password } }
        - name: DSN
          value: "host=127.0.0.1 port=5432 user=coroot password=$(PGPASSWORD) dbname=postgres connect_timeout=1 statement_timeout=10000"
  podAnnotations:
    prometheus.io/scrape: 'true'
    prometheus.io/port: '9090'
        </pre>

        <p>
            The database contains only the <var>products</var> table with 25 million rows (15Gb):
        </p>

        <pre v-hl.sql>
demo=# \d products
                              Table "public.products"
   Column    |  Type  | Collation | Nullable |               Default
-------------+--------+-----------+----------+--------------------------------------
 id          | bigint |           | not null | nextval('products_id_seq'::regclass)
 brand       | text   |           | not null |
 name        | text   |           | not null |
 model       | text   |           | not null |
 description | text   |           | not null |
 price       | bigint |           | not null |
Indexes:
    "products_pkey" PRIMARY KEY, btree (id)
    "idx_products_brand" btree (brand)
        </pre>

        <p>
            The <var>catalog</var> service handles requests from <var>loadgenerator</var> and retrieves 100 products from this table per request:
        </p>

        <pre v-hl.sql>
SELECT * FROM "products" WHERE "products"."id" IN (&lt;random IDs&gt;)
        </pre>

        <p>
            Under typical conditions, the database handles 200 queries per second, with a 99<sup>th</sup> percentile response time of 40 milliseconds.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/db-sli-ok.png" />
        </v-card>

        <h2>Failure #1</h2>

        <p>
            Let's execute a "heavy" query that will put a significant load on the CPU and disk of the Postgres instance:
        </p>

        <pre v-hl.sql>
select count(1) from products where description like '%LK-99%'
        </pre>

        <p>
            Now let's check how this impacted <var>catalog</var>'s performance and try to find the root cause,
            as if we're completely new to the problem.
        </p>

        <v-card class="pa-5 my-6 ma-auto" max-width="600px" >
            <v-img eager src="/static/img/blog/pg-issues/catalog-sli1.png" />
        </v-card>

        <p>
            We spot a spike in the <var>catalog</var>'s latency.
            To determine whether this anomaly is caused by a Postgres performance issue, we need to somehow compare the app latency
            with the DB latency. If these metrics correlate we can go deeper to find the reason for the degraded database performance.
        </p>

        <p>
            That's exactly how Coroot's automated root cause analysis works. Here's its report for this scenario.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/rca11.png" />
        </v-card>

        <p>
            As we can see, the <var>catalog</var> service responded slower because the database handled queries slower.
            Now Let's take a closer look at the metrics used for such analysis.
        </p>

        <h3>Postgres latency</h3>

        <p>
            Coroot's <a href="https://github.com/coroot/coroot-node-agent" target="_blank">node-agent</a> uses eBPF to capture
            requests made by one application to another.
            It exposes metrics describing their status and latency without the need to change any application code.
            The agent seamlessly works with common protocols like HTTP, Postgres, MySQL, Redis, Cassandra, MongoDB, and others.
        </p>

        <p>
            By capturing every Postgres query on the client side, Coroot is able to accurately measure the latency of queries
            just as the client experiences them.
            As a result, the agent provides query statistics for every client app, whether it works with a Postgres
            instance within the same cluster or an AWS RDS instance.
        </p>


        <ul>
            <li>
                <MetricLink name="container_postgres_queries_total" exporter="node-agent"/>
                - total number of outbound Postgres queries made by the container
            </li>
            <li>
                <MetricLink name="container_postgres_queries_duration_seconds_total" exporter="node-agent"/>
                - histogram of the response time for each outbound Postgres query
            </li>
        </ul>

        <p>
            Another advantage of leveraging eBPF to gather these metrics is that it doesn't require specific support from
            Postgres and stays compatible with all versions of Postgres.
            This compatibility is maintained because the
            <a href="https://www.postgresql.org/docs/current/protocol.html" target="_blank">Postgres Wire Protocol</a> has remained unchanged since PostgreSQL 7.4.
        </p>

        <h3>A lack of CPU time and its reasons</h3>

        <p>
            Let's imagine a basic outline of what Postgres is doing when executing a query:
        </p>

        <ul>
            <li>
                Getting data from memory (buffer cache) or disk (using the page cache).</li>
            <li>
                Doing calculations on a CPU, like filtering and sorting.
            </li>
            <li>
                Waiting for locks.
            </li>
        </ul>

        <p>That's about it! There's not much else happening!</p>

        <p>
            Returning to our failure, according to the Coroot RCA report, the first reason for the Postgres latency increase is a lack of CPU time.
            Let's go deeper and look at the metrics behind this inspection:
        </p>

        <ul>
            <li>
                <MetricLink name="container_resources_cpu_delay_seconds_total" exporter="node-agent"/>
                - total time duration the container has been waiting for a CPU (while being runnable).
                It provides a precise indication of whether a container faced CPU time shortages,
                regardless of the underlying cause — be it CPU throttling or insufficient CPU capacity on the node.
            </li>
            <li>
                <MetricLink name="container_resources_cpu_throttled_seconds_total" exporter="node-agent"/>
                - total time duration the container has been throttled for (limited in CPU time).
            </li>
            <li>
                <MetricLink name="pg_top_query_time_per_second" exporter="pg-agent"/>
                - time spent executing the Postgres query.
                This metric gathered from <var>pg_stat_activity</var> and <var>pg_stat_statement</var> and can explain
                anomalies in Postgres CPU consumption precise to particular queries.
            </li>
        </ul>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/cpu-throttling.png" />
        </v-card>

        <p>
            Using these metrics, we can infer that in our situation, the Postgres container faced a shortage of CPU time due to CPU throttling.
            This throttling seems to have been triggered by the increased CPU consumption, most probably originating from the following
            query executed by the user <var>postgres</var> on the <var>demo</var> database:
        </p>

        <pre v-hl.sql>
select count(?) from products where description like ?
        </pre>

        <p>
            This is the query we ran, but without specific arguments.
            The reason behind this is that Postgres doesn't include arguments in the query text when saving statistics to <var>pg_stat_statements</var>.
            Additionally, <var>coroot-pg-agent</var> adds another layer of security by obfuscating queries received from <var>pg_stat_activity</var> and <var>pg_stat_statements</var>.
        </p>

        <h3>I/O</h3>

        <p>
            The Coroot RCA report also highlighted that there's a second factor contributing to the raised database latency: elevated I/O latency.
            Now, let's dig deeper into this and examine the relevant metrics.
        </p>

        <ul>
            <li>
                <MetricLink name="node_resources_disk_(read|write)_time_seconds_total" exporter="node-agent"/>
                - total number of seconds spent reading and writing, including queue wait.
                To get the average I/O latency, the sum of these two should be normalized by the number of the executed I/O requests.
            </li>
            <li>
                <MetricLink name="node_resources_disk_(reads|writes)_total" exporter="node-agent"/>
                - total number of reads or writes completed successfully.
            </li>
            <li>
                <MetricLink name="node_resources_disk_io_time_seconds_total" exporter="node-agent"/>
                - total number of seconds the disk spent doing I/O. It doesn't include queue wait, only service time.
                E.g., if the derivative of this metric for a minute interval is 60s, this means that the disk was busy 100% of that interval.
            </li>
            <li>
                <MetricLink name="pg_top_query_io_time_per_second" exporter="pg-agent"/>
                - time the Postgres query spent awaiting I/O.
            </li>
        </ul>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/io.png" />
        </v-card>

        <p>
            Interpreting these metrics, we can grasp the situation: the Postgres server dedicated considerable time
            waiting for I/O requests to complete due to a high volume of read requests to its disk.
            This spike in read requests likely originated by the query:
        </p>

        <pre v-hl.sql>
select count(?) from products where description like ?
        </pre>

        <p>
            Bingo! We've identified the cause of the short Postgres latency spike without having to read its logs or dig into system views.
        </p>

        <h2>Failure #2</h2>

        <p>
            We've talked about two out of three possible reasons why a Postgres server could be handling queries more slowly than usual.
            Now, let's explore the scenario where a query is holding a lock, which in turn blocks other queries:
        </p>

        <pre v-hl.sql>
vacuum full products
        </pre>

        <p>
            In this case, it resulted in both higher latency and errors in processing requests by the <var>catalog</var> service.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/catalog-sli2.png" />
        </v-card>

        <p>
            As expected,  Coroot identified issues with the database, including errors and increased latency.
            The errors occur because the catalog sets the <a href="https://postgresqlco.nf/doc/en/param/statement_timeout/" target="_blank"><var>statement_timeout</var></a> when establishing a connection to Postgres.
            Additionally, Coroot recognized both I/O latency and blocked Postgres queries as potential causes.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/rca2.png" />
        </v-card>

        <p>
            In the previous example we've learned how to identify the causes behind I/O issues.
            Now, let's talk about Postgres errors and locks.
        </p>

        <h3>Postgres errors</h3>

        <p>
            It might seem a bit odd, but Postgres doesn't provide error statistics in its system views.
            At Coroot, we've discovered two methods to gather error counts, and we use both approaches simultaneously.
        </p>

        <p>
            The first method involves capturing queries and responses at the eBPF level.
            By parsing the Postgres Wire Protocol, the agent can determine if a specific query was successful or unsuccessful.
            As a result the <MetricLink name="container_postgres_queries_total" exporter="node-agent"/> metric is broken down by the <var>status</var> label.
        </p>

        <pre v-hl.json>
container_postgres_queries_total{
    container_id="/k8s/default/catalog-68878b5bfd-rnvt4/catalog", // the container initiated the query
    destination="10.43.101.67:5432", // initial connection destination, e.g. Kubernetes ClusterIP
    actual_destination="10.42.2.80:5432", // actual destination after NAT (iptables, IPVS, Cilium LB)
    status="failed", // or "ok"
}
        </pre>

        <p>
            Another method to gather error statistics involves parsing Postgres logs.
            This approach not only helps you identify specific errors but also enables tracking errors in Postgres background processes.
            Coroot <a href="/blog/mining-logs-from-unstructured-logs" target="_blank">automatically parses logs</a> of every
            container on the node, extracts recurring patterns, and exposes the <MetricLink name="container_log_messages_total" exporter="node-agent"/> metric.
        </p>

        <pre v-hl.json>
container_log_messages_total{
    container_id="/k8s/default/db-main-0/postgres",
    level="error",
    pattern_hash="e05043527d1e64109f71edc1dc3a817d",
    sample="UTC [35247] ERROR: canceling statement due to statement timeout",
    source="stdout/stderr",
}
        </pre>

        <p>
            This works seamlessly for containerized applications as well as for systemd services
            and <MetricLink name="aws_rds_log_messages_total" exporter="aws-agent" alias="AWS RDS instances"/>.
        </p>

        <h3>Postgres locks</h3>

        <p>
            Interestingly enough, when it comes to monitoring locks in Postgres, most tools suggest examining the count of locks obtained from the <var>pg_locks</var> view.
            However, Postgres DBAs often rely on their <a href="https://wiki.postgresql.org/wiki/Lock_Monitoring" target="_blank">black magic SQL queries</a>
            to identify the specific query causing a lock.
        </p>

        <p>
            To effectively troubleshoot locking-related issues, we only need to gather metrics that address two key questions:
        </p>

        <ul>
            <li>
                <i>Are there queries awaiting locks?</i>
                <p>
                    Using the <MetricLink name="pg_connections" exporter="pg-agent"/> metric enables Coroot to pinpoint the specific queries that are waiting for locks:
                </p>
                <pre v-hl.json>
pg_connections{
    namespace="default",
    pod="db-main-0",
    user="demo",
    db="demo",
    query="select * from products where id = any ( ? )",
    state="active",
    wait_event_type="Lock",
}
                </pre>
            </li>
            <li>
                <i>Which query is holding the lock?</i>

                <p>
                    The <MetricLink name="pg_lock_awaiting_queries" exporter="pg-agent"/> metric displays the number of queries waiting for the lock held by a specific query:
                </p>
                <pre v-hl.json>
pg_lock_awaiting_queries{
    namespace="default",
    pod="db-main-0",
    user="postgres",
    db="demo",
    blocking_query="vacuum full products",
}
                </pre>
            </li>
        </ul>


        <p>Getting back to our failure scenario, let's look at these metrics:</p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/locks.png" />
        </v-card>

        <p>
            Voilà! No need for guesswork or manual SQL queries – it's clear that the <var>vacuum full products</var> query is causing our application's queries to be blocked.
        </p>

        <h2>Failure #3</h2>

        <p>

            Another problem with most existing Postgres dashboards is that they show metrics for just one Postgres instance.
            If you use an HA cluster for fault tolerance or scalability, you'd want to monitor the entire cluster, not each instance separately.
            In my case, I'm using an HA Postgres cluster with three instances managed by <a href="https://github.com/zalando/patroni" target="_blank">Patroni</a>
            which handles failover and replication for all instances in the cluster.
        </p>

        <p>
            Let's simulate a failure of the primary Postgres instance to see the failover mechanism in action,
            and how it affects our client app.
            To achieve this, I'll use the following <a href="https://chaos-mesh.org/" target="_blank">Chaos Mesh</a> specification:
        </p>

        <pre v-hl.yaml>
kind: PodChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  namespace: default
  name: primary-pg-failure
spec:
  selector:
    namespaces:
      - default
    labelSelectors:
      spilo-role: master
  mode: all
  action: pod-failure
        </pre>

        <p>
            Given the time required to detect the unavailability of the primary instance, the failover took only 30 seconds.
            However, this short interruption inevitably affected the <var>catalog</var> service that was executing queries on the primary Postgres instance.
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/rca3.png" />
        </v-card>

        <p>
            To determine the current cluster role of each Postgres instance, Coroot utilizes metadata provided by the Postgres Operator through Pod labels:
        </p>

        <pre v-hl.json>
kube_pod_labels{
    label_cluster_name="db-main",
    label_spilo_role="master",
    namespace="default",
    pod="db-main-2",
}
        </pre>

        <p>
            As a result, every change in the cluster's topology can be detected:
        </p>

        <v-card class="pa-5 my-6">
            <v-img eager src="/static/img/blog/pg-issues/failover.png" />
        </v-card>

        <p>
            Now we understand that the original primary instance <var>db-main-2</var> became unavailable.
            Patroni detected this and promoted <var>db-main-0</var> to the role of the new primary.
        </p>

        <p>
            On another note, I want to emphasize how valuable log-based metrics are when dealing with previously unknown types of issues.
            Having a summary of the most relevant errors simplifies troubleshooting greatly.
        </p>


        <h2>Conclusion</h2>

        <p>
            The main takeaway from this post is that only the right "telling" metrics can help pinpoint the causes of problems
            over those that are easy to gather.
            The best way to tell them apart is by battle-testing them on real failure scenarios.
        </p>

        <p>
            The great news is that you don't have to go through this process on your own.
            We consistently apply this approach to develop Coroot, ensuring that any engineer using it can troubleshoot effectively.
        </p>

        <p>
            Try <router-link :to="{name: 'cloud'}">Coroot Cloud</router-link> now (14-day free trial is available) or follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to install Coroot Community Edition.
        </p>

        <p>If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ or share your experience on
            <a href="https://www.g2.com/products/coroot/reviews" target="_blank">G2</a>.</p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>
    </article>
</template>

<script>


import MetricLink from "@/components/MetricLink.vue";

export default {
    components: {MetricLink},
}
</script>

<style scoped>
</style>
